import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { useSolrContext } from "@e-fork/solr";
import ChevronDown from "@default/assets/ui/chevron-down-black.svg?url";
import scrollIntoViewWithOffset from "@default/lib/scroll-into-view-with-offset";
import DashedBorder from "@default/general-components/dashed-border";
import { AnimatePresence, motion } from "framer-motion";

export const getGroupId = (key) =>
  key ? `solr-group-${key.replace(/[^a-zA-Z0-9]/g, "-")}` : "";

const SCROLL_RETRY_INTERVAL = 200;
const SCROLL_RETRY_MAX_ATTEMPTS = 6;

/**
 * Scroll to an element, then keep watching for a bit and re-issue the scroll
 * if it stalls before reaching the target. Layout-shifting animations
 * elsewhere on the page (e.g. the bottom navigation's sponsor marquee
 * animating its height in) can cut a native smooth scroll short, so this
 * makes the jump resilient to that instead of depending on the page being
 * layout-quiet at the exact moment we scroll.
 */
const scrollToElementResiliently = (el, offset) => {
  let attempts = 0;
  let lastY = null;

  const getDistanceToTarget = () =>
    Math.abs(
      window.scrollY -
        (el.getBoundingClientRect().top -
          document.body.getBoundingClientRect().top -
          offset)
    );

  const stopWatching = () => {
    clearInterval(intervalId);
    window.removeEventListener("wheel", stopWatching);
    window.removeEventListener("touchstart", stopWatching);
  };

  const check = () => {
    if (!document.body.contains(el) || getDistanceToTarget() < 4) {
      stopWatching();
      return;
    }

    if (attempts > 0 && lastY !== null && Math.abs(window.scrollY - lastY) < 2) {
      // Scroll stalled before reaching the target - re-issue it.
      scrollIntoViewWithOffset(el, offset);
    }

    lastY = window.scrollY;
    attempts += 1;
    if (attempts >= SCROLL_RETRY_MAX_ATTEMPTS) stopWatching();
  };

  scrollIntoViewWithOffset(el, offset);
  const intervalId = setInterval(check, SCROLL_RETRY_INTERVAL);
  // Stop fighting the scroll as soon as the user tries to scroll themselves.
  window.addEventListener("wheel", stopWatching, { passive: true, once: true });
  window.addEventListener("touchstart", stopWatching, { passive: true, once: true });
};

const ScrollToFacet = ({ id, autoJumpToCurrentTime }) => {
  const { facets, items, status, metaData, fetchMore } = useSolrContext();
  const [activeKey, setActiveKey] = useState(null);
  const [target, setTarget] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef();
  const hasAutoJumped = useRef(false);

  const facet = facets?.find((f) => f.key === id);
  const options = facet?.results ?? [];

  // Once the overview has finished loading, jump to the current time.
  useEffect(() => {
    if (!autoJumpToCurrentTime || hasAutoJumped.current) return;
    if (!options.length || status === "LOADING") return;

    hasAutoJumped.current = true;

    const currentKey = moment().format("YYYY-MM-DDTHH");
    if (!options.some((option) => option.key === currentKey)) return;

    // Give the browser a moment to finish laying out the freshly rendered
    // teasers (images without reserved dimensions still shift the layout at
    // this point) before measuring the target position, otherwise the
    // computed offset is too small and the scroll stops short.
    const timeout = setTimeout(() => {
      const el = document.getElementById(getGroupId(currentKey));
      if (el) {
        scrollToElementResiliently(el, 130);
      } else {
        setTarget(currentKey);
      }
    }, 150);

    return () => clearTimeout(timeout);
  }, [autoJumpToCurrentTime, options, status]);

  // Track the active group via IntersectionObserver on rendered group wrappers.
  // Re-run whenever more items load so newly rendered groups get observed.
  useEffect(() => {
    if (!options.length) return;

    const observers = [];
    for (const option of options) {
      const el = document.getElementById(getGroupId(option.key));
      if (!el) continue;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveKey(option.key);
        },
        { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
      );
      observer.observe(el);
      observers.push({ observer, el });
    }

    return () =>
      observers.forEach(({ observer, el }) => observer.unobserve(el));
  }, [options, items.length]);

  // Load-until-found: fires each time items grow after a target is set.
  useEffect(() => {
    if (!target) return;

    const el = document.getElementById(getGroupId(target));
    if (el) {
      scrollToElementResiliently(el, 130);
      setTarget(null);
      return;
    }

    const newPage = Math.ceil(items.length / metaData.perPage);
    if (newPage >= metaData.pageCount) {
      setTarget(null);
      return;
    }

    fetchMore({
      updateConfig: (prev) => ({ ...prev, page: newPage }),
      updateData: (prev, next) => ({
        ...next,
        search_results: prev.search_results.concat(next.search_results),
      }),
    });
  }, [items.length, target]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  if (!options.length) return null;

  const activeLabel =
    options.find((o) => o.key === activeKey)?.label ?? options[0]?.label ?? "";

  const handleSelect = (key) => {
    setIsOpen(false);
    const el = document.getElementById(getGroupId(key));
    if (el) {
      scrollToElementResiliently(el, 130);
    } else {
      setTarget(key);
    }
  };

  return (
    <div ref={containerRef} className="scrollto-facet-wrapper">
      <button
        className="filter-control-element scrollspy"
        data-is-open={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <DashedBorder />
        <span className="body-m">{activeLabel}</span>
        <img className="chevron" src={ChevronDown} alt="" aria-hidden="true" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="custom-modal scrollspy-select"
            data-is-open={isOpen}
            key="scrollspy-select"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
          >
            <div className="values" aria-hidden={!isOpen}>
              {options.map(({ key, label }) => (
                <a
                  key={key}
                  href={`#${getGroupId(key)}`}
                  className="body-m"
                  tabIndex={isOpen ? "0" : "-1"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSelect(key);
                  }}
                >
                  {label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

ScrollToFacet.propTypes = {
  id: PropTypes.string.isRequired,
  autoJumpToCurrentTime: PropTypes.bool,
};

export default ScrollToFacet;
